'use client';

import { c as _c } from "react/compiler-runtime";
import type { PendingSidebarChatPrompt } from '@/lib/stores/chat-store';
import { AnimatePresence } from 'framer-motion';
import { AlertCircle, ArrowUpIcon, CirclePauseIcon, Trash2Icon } from 'lucide-react';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';
import { useEventCallback } from 'usehooks-ts';
import type { SidebarChatEngine } from './sidebar-chat-engine';
import type { AiContext } from './types/ai-context';
import type { SidebarChatMessage } from './types/sidebar-chat-message';
import { applySidebarInputToDraft, getInitialChatInput } from './chat-input-utils';
import { SymbolNavigationProvider } from './clickable-inline-code';
import { HELD_CHAT_MESSAGE_ID } from './sidebar-chat-engine';
import { buildSidebarChatModelBody } from './types/chat-request';
import { Queue, QueueItem, QueueItemAction, QueueItemActions, QueueItemContent, QueueItemDescription, QueueItemIndicator, QueueList, QueueSection, QueueSectionContent, QueueSectionLabel, QueueSectionTrigger } from '@/components/ai-elements/queue';
import { Shimmer } from '@/components/ai-elements/shimmer';
import { CHAT_COMPOSER_FRAME_CLASS } from '@/components/chat/chat-composer-skeleton';
import { FollowUpSuggestionChips } from '@/components/chat/follow-up-suggestions';
import { ChatMessageRenderer } from '@/components/chat/message';
import { MessageSkeletonList } from '@/components/chat/message-blocks/message-skeleton';
import { MultimodalInput } from '@/components/chat/multimodal-input';
import { TourQuickReply } from '@/components/chat/tour-quick-reply';
import { resolveChatModel } from '@/components/chat/use-chat-model-selection';
import { SESSION_REPLAY_MASK_PROPS } from '@/components/providers/session-replay-masking';
import { focusSidebarChatInput } from '@/components/ui/sidebar-chat-input';
import { useChatStore } from '@/lib/stores/chat-store';
import { getPrV2DiffFileElementId } from '@/lib/stores/navigation/diff-file-element-ids';
import { usePrStore } from '@/lib/stores/pr-store';
import { generateUUID } from '@/lib/utils';
import { sanitizeUIMessages } from '@/utils/chat';
type ChatErrorBannerProps = {
  error: Error | undefined;
};
function getChatErrorMessage(error: Error): string {
  const normalizedMessage = error.message?.toLowerCase() ?? '';
  if (normalizedMessage.includes('413') || normalizedMessage.includes('function_payload_too_large') || normalizedMessage.includes('payload too large')) {
    return 'The pull request and chat history are too large to send together. Please start a new chat or split the pull request.';
  }
  if (error.message?.includes('401')) {
    return 'Your session expired. Please refresh and try again.';
  }
  if (error.message?.includes('404')) {
    return 'Chat not found. Please refresh and try again.';
  }
  return 'We hit an unexpected error. Please try again.';
}
function ChatErrorBanner(t0) {
  const $ = _c(6);
  const {
    error
  } = t0;
  if (!error) {
    return null;
  }
  let t1;
  if ($[0] !== error) {
    t1 = getChatErrorMessage(error);
    $[0] = error;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const userMessage = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-sm font-medium">Something went wrong</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== userMessage) {
    t4 = <div role="alert" data-testid="chat-error-banner" className="flex w-full items-start gap-3 rounded-lg border border-red-800/30 bg-red-900/10 p-3 text-red-200">{t2}<div className="flex min-w-0 flex-1 flex-col gap-1">{t3}<span className="text-xs text-red-300/70">{userMessage}</span></div></div>;
    $[4] = userMessage;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
type AutoScrollOnThinkingProps = {
  status: string;
  lastMessageId: string | undefined;
  lastMessageScrollKey: string;
};
type QueuedPrompt = {
  id: string;
  modelId: string;
  state: 'blocked' | 'queued';
  text: string;
};
const CHAT_SIDEBAR_INSET_CLASS = 'px-3';
function isToolUIPart(part: SidebarChatMessage['parts'][number]): part is Extract<SidebarChatMessage['parts'][number], {
  type: `tool-${string}`;
}> {
  return part.type.startsWith('tool-');
}
function safeSerializeSignatureValue(value: unknown): string {
  try {
    return JSON.stringify(value ?? null) ?? 'null';
  } catch {
    return '[unserializable]';
  }
}
function getMessagePartRenderSignature(part: SidebarChatMessage['parts'][number], index: number): string {
  if (part.type === 'text') {
    return `text:${index}:${part.text}`;
  }
  if (part.type === 'reasoning') {
    return `reasoning:${index}:${part.state}:${part.text}`;
  }
  if (part.type === 'dynamic-tool') {
    return `dynamic:${index}:${part.toolName}:${part.state}:${safeSerializeSignatureValue(part.input)}:${safeSerializeSignatureValue(part.output)}:${part.errorText ?? ''}`;
  }
  if (part.type === 'step-start') {
    return `step:${index}`;
  }
  if (part.type === 'source-url') {
    return `source:${index}:${part.url}:${part.title ?? ''}`;
  }
  if (part.type === 'file') {
    return `file:${index}:${part.filename ?? ''}:${part.mediaType ?? ''}:${part.url}`;
  }
  if (isToolUIPart(part)) {
    return `${part.type}:${index}:${part.state ?? 'unknown'}:${part.toolCallId ?? 'unknown'}:${safeSerializeSignatureValue(part.input)}:${safeSerializeSignatureValue(part.output)}:${part.errorText ?? ''}`;
  }
  return `${part.type}:${index}`;
}
function getMessageRenderSignature(message: SidebarChatMessage): string {
  return [message.id, message.role, (message.parts ?? []).map((part, index) => getMessagePartRenderSignature(part, index)).join('|')].join(':');
}
function buildLastMessageScrollKey(message: SidebarChatMessage | undefined): string {
  if (!message) {
    return 'no-message';
  }
  if (!message.parts || message.parts.length === 0) {
    return `${message.id}:empty`;
  }
  const partSignature = message.parts.map((part, index) => getMessagePartRenderSignature(part, index)).join('|');
  return `${message.id}:${partSignature}`;
}
type MemoizedChatMessageRendererProps = {
  message: SidebarChatMessage;
  messageSignature: string;
};
const MemoizedChatMessageRenderer = memo(t0 => {
  const $ = _c(2);
  const {
    message
  } = t0;
  let t1;
  if ($[0] !== message) {
    t1 = <ChatMessageRenderer message={message} />;
    $[0] = message;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}, (prevProps, nextProps) => prevProps.messageSignature === nextProps.messageSignature);
MemoizedChatMessageRenderer.displayName = 'MemoizedChatMessageRenderer';

/** The last message is the person's, or the answer to it shows nothing yet. */
function isAwaitingFirstAnswerPart(messages: Array<SidebarChatMessage>): boolean {
  const lastMessage = messages.at(-1);
  return lastMessage !== undefined && (lastMessage.role !== 'assistant' || !lastMessage.parts?.length);
}
function removeInterruptedTurn(messages: Array<SidebarChatMessage>): Array<SidebarChatMessage> {
  const sanitizedMessages = sanitizeUIMessages(messages);
  const nextMessages = [...sanitizedMessages];
  if (nextMessages.at(-1)?.role === 'assistant') {
    nextMessages.pop();
  }
  if (nextMessages.at(-1)?.role === 'user') {
    nextMessages.pop();
  }
  return nextMessages;
}

// Takes prompts handed over from outside the sidebar (e.g. "Ask AI" on a draft comment),
// jumps to the bottom and focuses the input: the reader was looking at the diff and may have
// scrolled the chat up, and the chat may have mounted only now. Taking clears the store, so a
// re-run of the effect cannot hand a prompt over twice.
function PendingPromptHandoff(t0) {
  const $ = _c(6);
  const {
    onPrompts
  } = t0;
  const {
    scrollToBottom
  } = useStickToBottomContext();
  const hasPendingPrompts = useChatStore(_temp);
  const takePendingPrompts = useChatStore(_temp2);
  let t1;
  let t2;
  if ($[0] !== hasPendingPrompts || $[1] !== onPrompts || $[2] !== scrollToBottom || $[3] !== takePendingPrompts) {
    t1 = () => {
      if (!hasPendingPrompts) {
        return;
      }
      const prompts = takePendingPrompts();
      if (prompts.length === 0) {
        return;
      }
      onPrompts(prompts);
      scrollToBottom("smooth");
      focusSidebarChatInput();
    };
    t2 = [hasPendingPrompts, onPrompts, scrollToBottom, takePendingPrompts];
    $[0] = hasPendingPrompts;
    $[1] = onPrompts;
    $[2] = scrollToBottom;
    $[3] = takePendingPrompts;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2(state_0) {
  return state_0.takePendingPrompts;
}
function _temp(state) {
  return state.pendingPrompts.length > 0;
}
function AutoScrollOnThinking(t0) {
  const $ = _c(10);
  const {
    status,
    lastMessageId,
    lastMessageScrollKey
  } = t0;
  const {
    escapedFromLock,
    scrollToBottom
  } = useStickToBottomContext();
  let t1;
  if ($[0] !== escapedFromLock || $[1] !== scrollToBottom || $[2] !== status) {
    t1 = () => {
      if (status !== "streaming" || escapedFromLock) {
        return;
      }
      const animationFrame = requestAnimationFrame(() => {
        scrollToBottom("auto");
      });
      return () => cancelAnimationFrame(animationFrame);
    };
    $[0] = escapedFromLock;
    $[1] = scrollToBottom;
    $[2] = status;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== escapedFromLock || $[5] !== lastMessageId || $[6] !== lastMessageScrollKey || $[7] !== scrollToBottom || $[8] !== status) {
    t2 = [status, lastMessageId, lastMessageScrollKey, escapedFromLock, scrollToBottom];
    $[4] = escapedFromLock;
    $[5] = lastMessageId;
    $[6] = lastMessageScrollKey;
    $[7] = scrollToBottom;
    $[8] = status;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  useEffect(t1, t2);
  return null;
}
function NormalizeChatScrollGutter() {
  const $ = _c(3);
  const {
    scrollRef
  } = useStickToBottomContext();
  let t0;
  let t1;
  if ($[0] !== scrollRef) {
    t0 = () => {
      const scrollElement = scrollRef.current;
      if (!scrollElement || scrollElement.style.scrollbarGutter === "auto") {
        return;
      }
      scrollElement.style.scrollbarGutter = "auto";
    };
    t1 = [scrollRef];
    $[0] = scrollRef;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  return null;
}
function ResetFreshChatScrollPosition(t0) {
  const $ = _c(7);
  const {
    chatId,
    shouldReset
  } = t0;
  const {
    scrollRef
  } = useStickToBottomContext();
  let t1;
  if ($[0] !== scrollRef || $[1] !== shouldReset) {
    t1 = () => {
      if (!shouldReset) {
        return;
      }
      const resetScrollTop = () => {
        const scrollElement = scrollRef.current;
        if (!scrollElement) {
          return false;
        }
        scrollElement.scrollTop = 0;
        return true;
      };
      resetScrollTop();
      const animationFrame = requestAnimationFrame(() => {
        resetScrollTop();
      });
      return () => cancelAnimationFrame(animationFrame);
    };
    $[0] = scrollRef;
    $[1] = shouldReset;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== chatId || $[4] !== scrollRef || $[5] !== shouldReset) {
    t2 = [chatId, scrollRef, shouldReset];
    $[3] = chatId;
    $[4] = scrollRef;
    $[5] = shouldReset;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useLayoutEffect(t1, t2);
  return null;
}
type ChatRunnerViewProps = {
  id: string;
  /** The conversation the chat opened with, if it was saved. */
  initialMessages: Array<SidebarChatMessage>;
  aiContext: AiContext;
  engine: SidebarChatEngine;
  /** The answer that completed in this session, which offers follow-ups. */
  liveFinishedMessageId: string | null;
  /** The chat's saved conversation is loading: only the message list waits for it. */
  historyLoading?: boolean;
  /** Loading the saved conversation failed: the message list offers to try again. */
  onRetryHistory?: (() => void) | null;
  /** What the composer's footer shows beside the model picker, such as the agent's chip. */
  composerTools?: ReactNode;
  /** A one-line status just above the composer, such as the agent's sandbox starting. */
  composerStatus?: ReactNode;
};

/** The sidebar chat's transcript, queue and composer, on whichever engine answers it. */
export function ChatRunnerView(t0) {
  const $ = _c(151);
  const {
    id,
    initialMessages,
    aiContext,
    engine,
    liveFinishedMessageId,
    historyLoading: t1,
    onRetryHistory: t2,
    composerTools,
    composerStatus
  } = t0;
  const historyLoading = t1 === undefined ? false : t1;
  const onRetryHistory = t2 === undefined ? null : t2;
  const setChatId = useChatStore(_temp3);
  const setChatPersisted = useChatStore(_temp4);
  const setStoredMessages = useChatStore(_temp5);
  const setStoredStatus = useChatStore(_temp6);
  const selectedText = useChatStore(_temp7);
  const selectedTextMode = useChatStore(_temp8);
  const clearSelectedText = useChatStore(_temp9);
  const {
    owner
  } = usePrStore();
  const activeQueuedDispatchRef = useRef(null);
  let t3;
  if ($[0] !== id) {
    t3 = () => getInitialChatInput({
      chatId: id,
      sidebarInput: "",
      mode: "replace"
    });
    $[0] = id;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [input, setInput] = useState(t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [queuedPrompts, setQueuedPrompts] = useState(t4);
  const [queuePaused, setQueuePaused] = useState(false);
  const comparisonHeadSha = aiContext.headSha ?? aiContext.pr?.head?.sha ?? null;
  const comparisonBaseSha = aiContext.baseSha ?? aiContext.pr?.base?.sha ?? null;
  let t5;
  if ($[3] !== clearSelectedText || $[4] !== selectedTextMode) {
    t5 = nextSelectedText => {
      setInput(currentInput => applySidebarInputToDraft(currentInput, nextSelectedText, selectedTextMode));
      clearSelectedText();
    };
    $[3] = clearSelectedText;
    $[4] = selectedTextMode;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const applySelectedTextToDraft = useEventCallback(t5);
  const {
    messages,
    setMessages,
    sendMessage,
    status,
    stop,
    error,
    clearError
  } = engine;
  let lastMessage;
  let t6;
  if ($[6] !== messages) {
    lastMessage = messages.at(-1);
    t6 = buildLastMessageScrollKey(lastMessage);
    $[6] = messages;
    $[7] = lastMessage;
    $[8] = t6;
  } else {
    lastMessage = $[7];
    t6 = $[8];
  }
  const lastMessageScrollKey = t6;
  const shouldResetFreshChatScroll = messages.length === 0 && status === "ready";
  const isLoading = status === "submitted" || status === "streaming";
  let t7;
  if ($[9] !== owner) {
    t7 = () => resolveChatModel(owner);
    $[9] = owner;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const resolveSendModel = t7;
  let t8;
  if ($[11] !== resolveSendModel || $[12] !== sendMessage) {
    t8 = text => {
      sendMessage({
        text
      }, {
        body: buildSidebarChatModelBody(resolveSendModel())
      });
    };
    $[11] = resolveSendModel;
    $[12] = sendMessage;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleQuickReplySend = t8;
  const showQuickReplies = !isLoading && queuedPrompts.length === 0 && lastMessage?.role === "assistant";
  let t10;
  let t9;
  if ($[14] !== id || $[15] !== setChatId) {
    t9 = () => {
      setChatId(id);
    };
    t10 = [id, setChatId];
    $[14] = id;
    $[15] = setChatId;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[18] !== messages || $[19] !== setStoredMessages) {
    t11 = () => {
      setStoredMessages(messages);
    };
    t12 = [messages, setStoredMessages];
    $[18] = messages;
    $[19] = setStoredMessages;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[22] !== setStoredStatus || $[23] !== status) {
    t13 = () => {
      setStoredStatus(status);
    };
    t14 = [setStoredStatus, status];
    $[22] = setStoredStatus;
    $[23] = status;
    $[24] = t13;
    $[25] = t14;
  } else {
    t13 = $[24];
    t14 = $[25];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[26] !== initialMessages || $[27] !== setChatPersisted) {
    t15 = () => {
      const hasInitialMessages = initialMessages && initialMessages.length > 0;
      if (hasInitialMessages) {
        setChatPersisted(true);
      }
    };
    t16 = [initialMessages, setChatPersisted];
    $[26] = initialMessages;
    $[27] = setChatPersisted;
    $[28] = t15;
    $[29] = t16;
  } else {
    t15 = $[28];
    t16 = $[29];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[30] !== applySelectedTextToDraft || $[31] !== selectedText) {
    t17 = () => {
      if (selectedText) {
        applySelectedTextToDraft(selectedText);
      }
    };
    t18 = [applySelectedTextToDraft, selectedText];
    $[30] = applySelectedTextToDraft;
    $[31] = selectedText;
    $[32] = t17;
    $[33] = t18;
  } else {
    t17 = $[32];
    t18 = $[33];
  }
  useLayoutEffect(t17, t18);
  const [isHydrated, setIsHydrated] = useState(false);
  let t19;
  let t20;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      queueMicrotask(() => {
        setIsHydrated(true);
      });
    };
    t20 = [];
    $[34] = t19;
    $[35] = t20;
  } else {
    t19 = $[34];
    t20 = $[35];
  }
  useEffect(t19, t20);
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = prompt => {
      setQueuedPrompts(currentPrompts => currentPrompts.concat({
        id: generateUUID(),
        modelId: prompt.modelId,
        state: "queued",
        text: prompt.text
      }));
    };
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  const queuePrompt = useEventCallback(t21);
  let t22;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = prompt_0 => {
      setQueuePaused(true);
      setQueuedPrompts(currentPrompts_0 => {
        const existingPrompt = currentPrompts_0.find(queuedPrompt => queuedPrompt.id === prompt_0.id);
        if (existingPrompt) {
          return currentPrompts_0.map(queuedPrompt_0 => queuedPrompt_0.id === prompt_0.id ? {
            ...queuedPrompt_0,
            state: "blocked"
          } : queuedPrompt_0);
        }
        return [{
          ...prompt_0,
          state: "blocked"
        }, ...currentPrompts_0];
      });
    };
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const blockQueuedPrompt = useEventCallback(t22);
  let t23;
  if ($[38] !== clearError) {
    t23 = promptId => {
      let removedBlockedHead = false;
      setQueuedPrompts(currentPrompts_1 => {
        removedBlockedHead = currentPrompts_1[0]?.id === promptId && currentPrompts_1[0].state === "blocked";
        return currentPrompts_1.filter(queuedPrompt_1 => queuedPrompt_1.id !== promptId);
      });
      if (!removedBlockedHead) {
        return;
      }
      setQueuePaused(false);
      clearError();
    };
    $[38] = clearError;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  const removeQueuedPrompt = useEventCallback(t23);
  let t24;
  if ($[40] !== blockQueuedPrompt || $[41] !== sendMessage) {
    t24 = prompt_1 => {
      activeQueuedDispatchRef.current = prompt_1;
      setQueuedPrompts(currentPrompts_2 => currentPrompts_2.filter(queuedPrompt_2 => queuedPrompt_2.id !== prompt_1.id));
      sendMessage({
        text: prompt_1.text
      }, {
        body: buildSidebarChatModelBody(prompt_1.modelId)
      }).catch(() => {
        const activeDispatch = activeQueuedDispatchRef.current;
        if (!activeDispatch || activeDispatch.id !== prompt_1.id) {
          return;
        }
        blockQueuedPrompt(prompt_1);
      });
    };
    $[40] = blockQueuedPrompt;
    $[41] = sendMessage;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const dispatchQueuedPrompt = useEventCallback(t24);
  let t25;
  if ($[43] !== clearError || $[44] !== dispatchQueuedPrompt || $[45] !== setMessages || $[46] !== status || $[47] !== stop) {
    t25 = promptId_0 => {
      let nextPrompt = null;
      setQueuedPrompts(currentPrompts_3 => {
        const selectedPrompt = currentPrompts_3.find(queuedPrompt_3 => queuedPrompt_3.id === promptId_0);
        if (!selectedPrompt) {
          return currentPrompts_3;
        }
        nextPrompt = {
          ...selectedPrompt,
          state: "queued"
        };
        return [nextPrompt, ...currentPrompts_3.filter(queuedPrompt_4 => queuedPrompt_4.id !== promptId_0)];
      });
      if (!nextPrompt) {
        return;
      }
      setQueuePaused(false);
      clearError();
      if ((status === "ready" || status === "error") && !activeQueuedDispatchRef.current) {
        dispatchQueuedPrompt(nextPrompt);
        return;
      }
      setMessages(_temp10);
      stop();
    };
    $[43] = clearError;
    $[44] = dispatchQueuedPrompt;
    $[45] = setMessages;
    $[46] = status;
    $[47] = stop;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  const sendQueuedPromptNow = useEventCallback(t25);
  let t26;
  if ($[49] !== isLoading || $[50] !== queuePrompt || $[51] !== queuedPrompts.length || $[52] !== resolveSendModel || $[53] !== sendMessage) {
    t26 = prompts => {
      const model = resolveSendModel();
      const canSendFirstNow = !isLoading && queuedPrompts.length === 0;
      prompts.forEach((prompt_2, index) => {
        const text_0 = typeof prompt_2 === "string" ? prompt_2 : prompt_2.text;
        const modelId = typeof prompt_2 === "string" ? model : prompt_2.modelId;
        if (index === 0 && canSendFirstNow) {
          sendMessage({
            text: text_0
          }, {
            body: buildSidebarChatModelBody(modelId)
          });
          return;
        }
        queuePrompt({
          modelId,
          text: text_0
        });
      });
    };
    $[49] = isLoading;
    $[50] = queuePrompt;
    $[51] = queuedPrompts.length;
    $[52] = resolveSendModel;
    $[53] = sendMessage;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  const sendHandedOverPrompts = useEventCallback(t26);
  let t27;
  if ($[55] !== dispatchQueuedPrompt || $[56] !== queuePaused || $[57] !== queuedPrompts[0] || $[58] !== status) {
    t27 = () => {
      if (status !== "ready" || queuePaused) {
        return;
      }
      const nextPrompt_0 = queuedPrompts[0];
      if (!nextPrompt_0 || nextPrompt_0.state !== "queued") {
        return;
      }
      if (activeQueuedDispatchRef.current?.id === nextPrompt_0.id) {
        return;
      }
      dispatchQueuedPrompt(nextPrompt_0);
    };
    $[55] = dispatchQueuedPrompt;
    $[56] = queuePaused;
    $[57] = queuedPrompts[0];
    $[58] = status;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== dispatchQueuedPrompt || $[61] !== queuePaused || $[62] !== queuedPrompts || $[63] !== status) {
    t28 = [dispatchQueuedPrompt, queuePaused, queuedPrompts, status];
    $[60] = dispatchQueuedPrompt;
    $[61] = queuePaused;
    $[62] = queuedPrompts;
    $[63] = status;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  useEffect(t27, t28);
  let t29;
  let t30;
  if ($[65] !== blockQueuedPrompt || $[66] !== status) {
    t29 = () => {
      if (status === "ready") {
        activeQueuedDispatchRef.current = null;
        return;
      }
      if (status !== "error") {
        return;
      }
      const activeDispatch_0 = activeQueuedDispatchRef.current;
      if (activeDispatch_0) {
        blockQueuedPrompt(activeDispatch_0);
      }
      activeQueuedDispatchRef.current = null;
    };
    t30 = [blockQueuedPrompt, status];
    $[65] = blockQueuedPrompt;
    $[66] = status;
    $[67] = t29;
    $[68] = t30;
  } else {
    t29 = $[67];
    t30 = $[68];
  }
  useEffect(t29, t30);
  let t31;
  if ($[69] !== aiContext.codeContent) {
    t31 = aiContext.codeContent ?? [];
    $[69] = aiContext.codeContent;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  const codeContent = t31;
  const preferredNavigationFilename = aiContext.activeFilename ?? aiContext.visibleFilename;
  let t32;
  bb0: {
    if (!preferredNavigationFilename) {
      t32 = codeContent;
      break bb0;
    }
    let t33;
    if ($[71] !== codeContent || $[72] !== preferredNavigationFilename) {
      let t34;
      if ($[74] !== preferredNavigationFilename) {
        t34 = file => file.filename === preferredNavigationFilename;
        $[74] = preferredNavigationFilename;
        $[75] = t34;
      } else {
        t34 = $[75];
      }
      t33 = codeContent.find(t34);
      $[71] = codeContent;
      $[72] = preferredNavigationFilename;
      $[73] = t33;
    } else {
      t33 = $[73];
    }
    const preferredFile = t33;
    let t34;
    if ($[76] !== codeContent || $[77] !== preferredFile) {
      t34 = preferredFile ? [preferredFile] : codeContent;
      $[76] = codeContent;
      $[77] = preferredFile;
      $[78] = t34;
    } else {
      t34 = $[78];
    }
    t32 = t34;
  }
  const preferredNavigationFiles = t32;
  let t33;
  if ($[79] !== preferredNavigationFilename) {
    t33 = preferredNavigationFilename ? getPrV2DiffFileElementId(preferredNavigationFilename) : undefined;
    $[79] = preferredNavigationFilename;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  const navigationOriginElementId = t33;
  let t34;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <NormalizeChatScrollGutter />;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== id || $[83] !== shouldResetFreshChatScroll) {
    t35 = <ResetFreshChatScrollPosition chatId={id} shouldReset={shouldResetFreshChatScroll} />;
    $[82] = id;
    $[83] = shouldResetFreshChatScroll;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] !== sendHandedOverPrompts) {
    t36 = <PendingPromptHandoff onPrompts={sendHandedOverPrompts} />;
    $[85] = sendHandedOverPrompts;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  const t37 = lastMessage?.id;
  let t38;
  if ($[87] !== lastMessageScrollKey || $[88] !== status || $[89] !== t37) {
    t38 = <AutoScrollOnThinking status={status} lastMessageId={t37} lastMessageScrollKey={lastMessageScrollKey} />;
    $[87] = lastMessageScrollKey;
    $[88] = status;
    $[89] = t37;
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  let t39;
  if ($[91] !== error) {
    t39 = <ChatErrorBanner error={error} />;
    $[91] = error;
    $[92] = t39;
  } else {
    t39 = $[92];
  }
  let t40;
  if ($[93] !== historyLoading || $[94] !== isHydrated || $[95] !== messages || $[96] !== onRetryHistory) {
    t40 = <ChatMessageList messages={messages} isHydrated={isHydrated} historyLoading={historyLoading} onRetryHistory={onRetryHistory} />;
    $[93] = historyLoading;
    $[94] = isHydrated;
    $[95] = messages;
    $[96] = onRetryHistory;
    $[97] = t40;
  } else {
    t40 = $[97];
  }
  let t41;
  if ($[98] !== isLoading || $[99] !== messages) {
    t41 = isLoading && isAwaitingFirstAnswerPart(messages) && <div className="flex min-w-0 items-center"><Shimmer as="span" className="text-xs text-muted-foreground" duration={1}>Thinking…</Shimmer></div>;
    $[98] = isLoading;
    $[99] = messages;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] !== aiContext || $[102] !== handleQuickReplySend || $[103] !== id || $[104] !== input || $[105] !== lastMessage || $[106] !== liveFinishedMessageId || $[107] !== messages || $[108] !== showQuickReplies) {
    t42 = showQuickReplies ? <><TourQuickReply lastMessage={lastMessage} onSend={handleQuickReplySend} />{lastMessage?.id === liveFinishedMessageId ? <FollowUpSuggestionChips chatId={id} aiContext={aiContext} messages={messages} hidden={input.trim().length > 0} onSend={handleQuickReplySend} /> : null}</> : null;
    $[101] = aiContext;
    $[102] = handleQuickReplySend;
    $[103] = id;
    $[104] = input;
    $[105] = lastMessage;
    $[106] = liveFinishedMessageId;
    $[107] = messages;
    $[108] = showQuickReplies;
    $[109] = t42;
  } else {
    t42 = $[109];
  }
  let t43;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <div className="min-h-[24px] min-w-[24px] shrink-0" />;
    $[110] = t43;
  } else {
    t43 = $[110];
  }
  let t44;
  if ($[111] !== t39 || $[112] !== t40 || $[113] !== t41 || $[114] !== t42) {
    t44 = <div className={`flex min-h-0 min-w-0 flex-1 flex-col gap-6 ${CHAT_SIDEBAR_INSET_CLASS} pt-4`}>{t39}{t40}{t41}{t42}{t43}</div>;
    $[111] = t39;
    $[112] = t40;
    $[113] = t41;
    $[114] = t42;
    $[115] = t44;
  } else {
    t44 = $[115];
  }
  let t45;
  if ($[116] !== t36 || $[117] !== t38 || $[118] !== t44) {
    t45 = <StickToBottom.Content scrollClassName="custom-scrollbar overflow-y-auto overflow-x-hidden [mask-image:linear-gradient(to_bottom,transparent,black_16px)]" className="flex min-h-0 flex-1 flex-col">{t36}{t38}{t44}</StickToBottom.Content>;
    $[116] = t36;
    $[117] = t38;
    $[118] = t44;
    $[119] = t45;
  } else {
    t45 = $[119];
  }
  let t46;
  if ($[120] !== t35 || $[121] !== t45) {
    t46 = <StickToBottom className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden" resize="smooth" initial="smooth">{t34}{t35}{t45}</StickToBottom>;
    $[120] = t35;
    $[121] = t45;
    $[122] = t46;
  } else {
    t46 = $[122];
  }
  let t47;
  if ($[123] !== queuePaused || $[124] !== queuedPrompts || $[125] !== removeQueuedPrompt || $[126] !== sendQueuedPromptNow) {
    t47 = queuedPrompts.length > 0 ? <QueuedPromptsPanel queuedPrompts={queuedPrompts} paused={queuePaused} onRemove={removeQueuedPrompt} onSendNow={sendQueuedPromptNow} /> : undefined;
    $[123] = queuePaused;
    $[124] = queuedPrompts;
    $[125] = removeQueuedPrompt;
    $[126] = sendQueuedPromptNow;
    $[127] = t47;
  } else {
    t47 = $[127];
  }
  const t48 = messages.length === 0 && !historyLoading && !onRetryHistory;
  let t49;
  if ($[128] !== aiContext || $[129] !== composerStatus || $[130] !== composerTools || $[131] !== id || $[132] !== input || $[133] !== isLoading || $[134] !== queuePrompt || $[135] !== sendMessage || $[136] !== setMessages || $[137] !== stop || $[138] !== t47 || $[139] !== t48) {
    t49 = <div className={CHAT_COMPOSER_FRAME_CLASS}><form className="flex w-full gap-2 bg-background"><MultimodalInput chatId={id} input={input} setInput={setInput} isLoading={isLoading} stop={stop} setMessages={setMessages} sendMessage={sendMessage} queuePrompt={queuePrompt} queueContent={t47} aiContext={aiContext} files={aiContext.files} conversationEmpty={t48} tools={composerTools} status={composerStatus} /></form></div>;
    $[128] = aiContext;
    $[129] = composerStatus;
    $[130] = composerTools;
    $[131] = id;
    $[132] = input;
    $[133] = isLoading;
    $[134] = queuePrompt;
    $[135] = sendMessage;
    $[136] = setMessages;
    $[137] = stop;
    $[138] = t47;
    $[139] = t48;
    $[140] = t49;
  } else {
    t49 = $[140];
  }
  let t50;
  if ($[141] !== t46 || $[142] !== t49) {
    t50 = <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background" {...SESSION_REPLAY_MASK_PROPS}>{t46}{t49}</div>;
    $[141] = t46;
    $[142] = t49;
    $[143] = t50;
  } else {
    t50 = $[143];
  }
  let t51;
  if ($[144] !== codeContent || $[145] !== comparisonBaseSha || $[146] !== comparisonHeadSha || $[147] !== navigationOriginElementId || $[148] !== preferredNavigationFiles || $[149] !== t50) {
    t51 = <SymbolNavigationProvider codeContent={codeContent} headSha={comparisonHeadSha} baseSha={comparisonBaseSha} preferredFiles={preferredNavigationFiles} originElementId={navigationOriginElementId}>{t50}</SymbolNavigationProvider>;
    $[144] = codeContent;
    $[145] = comparisonBaseSha;
    $[146] = comparisonHeadSha;
    $[147] = navigationOriginElementId;
    $[148] = preferredNavigationFiles;
    $[149] = t50;
    $[150] = t51;
  } else {
    t51 = $[150];
  }
  return t51;
}

/**
 * The conversation: its messages, or while the chat's saved conversation loads (and briefly as a
 * restored chat mounts) placeholders, or the offer to load it again.
 */
function _temp10(messages_0) {
  return removeInterruptedTurn(messages_0);
}
function _temp9(state_5) {
  return state_5.clearSelectedText;
}
function _temp8(state_4) {
  return state_4.selectedTextMode;
}
function _temp7(state_3) {
  return state_3.selectedText;
}
function _temp6(state_2) {
  return state_2.setStatus;
}
function _temp5(state_1) {
  return state_1.setMessages;
}
function _temp4(state_0) {
  return state_0.setChatPersisted;
}
function _temp3(state) {
  return state.setChatId;
}
function ChatMessageList(t0) {
  const $ = _c(14);
  const {
    messages,
    isHydrated,
    historyLoading,
    onRetryHistory
  } = t0;
  let t1;
  if ($[0] !== messages) {
    t1 = messages.filter(_temp11);
    $[0] = messages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const historicCount = t1.length;
  const placeholders = historyLoading ? 3 : isHydrated ? 0 : Math.min(historicCount, 3);
  const waiting = onRetryHistory !== null || placeholders > 0;
  let t2;
  if ($[2] !== messages || $[3] !== waiting) {
    t2 = waiting ? messages.filter(_temp12) : messages;
    $[2] = messages;
    $[3] = waiting;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const shownMessages = t2;
  let t3;
  if ($[5] !== shownMessages) {
    t3 = shownMessages.map(_temp13);
    $[5] = shownMessages;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const messageNodes = t3;
  let t4;
  if ($[7] !== messageNodes || $[8] !== onRetryHistory || $[9] !== placeholders || $[10] !== waiting) {
    t4 = waiting ? <div key="history-wait" className="flex min-w-0 flex-col gap-6">{onRetryHistory ? <ChatHistoryError onRetry={onRetryHistory} /> : <MessageSkeletonList count={placeholders} />}{messageNodes}</div> : messageNodes;
    $[7] = messageNodes;
    $[8] = onRetryHistory;
    $[9] = placeholders;
    $[10] = waiting;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <AnimatePresence mode="wait">{t4}</AnimatePresence>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}

/** The prompts waiting for the answer in flight, above the composer, each removable or sendable now. */
function _temp13(message_1) {
  return <MemoizedChatMessageRenderer key={message_1.id} message={message_1} messageSignature={getMessageRenderSignature(message_1)} />;
}
function _temp12(message_0) {
  return message_0.id === HELD_CHAT_MESSAGE_ID;
}
function _temp11(message) {
  return message.id !== HELD_CHAT_MESSAGE_ID;
}
function QueuedPromptsPanel(t0) {
  const $ = _c(19);
  const {
    queuedPrompts,
    paused,
    onRemove,
    onSendNow
  } = t0;
  let t1;
  if ($[0] !== queuedPrompts.length) {
    t1 = <QueueSectionLabel count={queuedPrompts.length} label="Queued" className="gap-1.5 text-[11px] font-medium text-muted-foreground [&>span:last-child]:text-[11px]" />;
    $[0] = queuedPrompts.length;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== paused) {
    t2 = paused ? <div className="flex items-center gap-1 text-[11px] text-amber-600"><CirclePauseIcon className="size-3.5" /><span>Paused</span></div> : null;
    $[2] = paused;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <QueueSectionTrigger className="rounded-none bg-transparent px-0 py-0 text-xs hover:bg-transparent">{t1}{t2}</QueueSectionTrigger>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onRemove || $[8] !== onSendNow || $[9] !== queuedPrompts) {
    let t5;
    if ($[11] !== onRemove || $[12] !== onSendNow) {
      t5 = queuedPrompt => <QueueItem key={queuedPrompt.id} className={queuedPrompt.state === "blocked" ? "rounded-sm bg-amber-950/10" : "rounded-sm"}><div className="flex w-full items-center gap-2"><QueueItemIndicator className={queuedPrompt.state === "blocked" ? "mt-0 border-amber-500/60 bg-amber-500/15" : "mt-0 border-muted-foreground/35"} /><div className="min-w-0 flex-1"><QueueItemContent className={queuedPrompt.state === "blocked" ? "text-amber-100" : "text-foreground/90"}>{queuedPrompt.text}</QueueItemContent>{queuedPrompt.state === "blocked" ? <QueueItemDescription className="ml-0 mt-0.5 text-amber-200/80">Blocked</QueueItemDescription> : null}</div><QueueItemActions><QueueItemAction aria-label={`Remove queued prompt: ${queuedPrompt.text}`} className="self-center" onClick={() => {
              onRemove(queuedPrompt.id);
            }}><Trash2Icon className="size-3.5" /></QueueItemAction><QueueItemAction aria-label={`Send queued prompt now: ${queuedPrompt.text}`} className="self-center" onClick={() => {
              onSendNow(queuedPrompt.id);
            }}><ArrowUpIcon className="size-3.5" /></QueueItemAction></QueueItemActions></div></QueueItem>;
      $[11] = onRemove;
      $[12] = onSendNow;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t4 = queuedPrompts.map(t5);
    $[7] = onRemove;
    $[8] = onSendNow;
    $[9] = queuedPrompts;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = <QueueSectionContent><QueueList className="mt-1 mb-0">{t4}</QueueList></QueueSectionContent>;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t3 || $[17] !== t5) {
    t6 = <Queue className="mx-auto max-h-[150px] w-[95%] overflow-hidden rounded-b-none border-input border-b-0 bg-black/20 px-2.5 pt-2 pb-1.5 shadow-none"><QueueSection defaultOpen={true}>{t3}{t5}</QueueSection></Queue>;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}

/** The saved conversation could not load: try again, or start a new conversation. */
function ChatHistoryError(t0) {
  const $ = _c(10);
  const {
    onRetry
  } = t0;
  const startNewChat = useChatStore(_temp14);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AlertCircle className="size-5 text-muted-foreground" />;
    t2 = <p className="text-sm text-muted-foreground">Failed to load conversation</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onRetry) {
    t3 = <button type="button" onClick={onRetry} className="text-sm text-primary underline underline-offset-4 hover:text-primary/80">Try again</button>;
    $[2] = onRetry;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="text-sm text-muted-foreground">or</span>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== startNewChat) {
    t5 = <button type="button" onClick={() => startNewChat()} className="text-sm text-primary underline underline-offset-4 hover:text-primary/80">Start new conversation</button>;
    $[5] = startNewChat;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <div data-testid="chat-hydration-error" className="flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-3">{t1}{t2}<div className="flex items-center gap-3">{t3}{t4}{t5}</div></div>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function _temp14(state) {
  return state.startNewChat;
}